// ===================================================================
// KAIXO - Pestaña Plataformas
// ===================================================================
// Lista todas las plataformas externas que KAIXO usa, con su modo de
// pago (fijo / por consumo / híbrido), coste mensual y último refresh.
//
// Funcionalidades:
//   · Refresh manual por plataforma (llama a la API real del proveedor)
//   · Refresh masivo de todas las que tienen adapter
//   · Editar plan fijo o cualquier campo
//   · Crear nueva plataforma
//   · Borrar
//   · Suma total mensual estimada en EUR (con conversión USD/GBP→EUR)
//
// Solo accesible para admin_general (la pestaña entera está restringida).
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
    adminPlatformsList,
    adminPlatformCreate,
    adminPlatformUpdate,
    adminPlatformDelete,
    adminPlatformRefresh,
    adminPlatformsRefreshAll
} from '../../../services/adminApi';
import './AdminPlatformsSection.css';

const CATEGORIES = [
    { id: 'hosting', label: 'Hosting' },
    { id: 'database', label: 'Base de datos' },
    { id: 'ai', label: 'IA / ML' },
    { id: 'payments', label: 'Pagos' },
    { id: 'media', label: 'Media' },
    { id: 'monitoring', label: 'Monitoreo' },
    { id: 'communications', label: 'Comunicaciones' },
    { id: 'auth', label: 'Auth' },
    { id: 'other', label: 'Otra' }
];

const PRICING_LABELS = {
    fixed: { label: 'Plan fijo', color: '#3b82f6' },
    usage: { label: 'Por consumo', color: '#F5A000' },
    hybrid: { label: 'Híbrido', color: '#22c55e' }
};

function fmtMoney(cents, currency = 'EUR') {
    if (cents == null) return '—';
    const sym = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
    return `${sym}${(cents / 100).toFixed(2)}`;
}

function fmtRelativeTime(date) {
    if (!date) return 'nunca';
    const ms = Date.now() - new Date(date).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `hace ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `hace ${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h}h`;
    return `hace ${Math.floor(h / 24)}d`;
}

export default function AdminPlatformsSection() {
    const [platforms, setPlatforms] = useState([]);
    const [totalEurCents, setTotalEurCents] = useState(0);
    const [availableAdapters, setAvailableAdapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshingId, setRefreshingId] = useState(null);
    const [refreshingAll, setRefreshingAll] = useState(false);
    const [editing, setEditing] = useState(null); // platform object | { __new: true }

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await adminPlatformsList();
            setPlatforms(data?.platforms || []);
            setTotalEurCents(data?.totalMonthlyEurCents || 0);
            setAvailableAdapters(data?.availableAdapters || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const refreshOne = async (id) => {
        setRefreshingId(id);
        try {
            await adminPlatformRefresh(id);
            await reload();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setRefreshingId(null);
        }
    };

    const refreshAll = async () => {
        setRefreshingAll(true);
        try {
            await adminPlatformsRefreshAll();
            await reload();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setRefreshingAll(false);
        }
    };

    const removeOne = async (p) => {
        if (!window.confirm(`¿Eliminar la plataforma "${p.name}"?`)) return;
        try {
            await adminPlatformDelete(p._id);
            reload();
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        }
    };

    // Última fecha de refresh global (la más reciente de todas)
    const lastGlobalRefresh = platforms.reduce((acc, p) => {
        if (!p.lastRefreshedAt) return acc;
        const t = new Date(p.lastRefreshedAt).getTime();
        return t > acc ? t : acc;
    }, 0);

    return (
        <div className="apl-wrap">
            {/* Cabecera con total y refresh global */}
            <div className="apl-header">
                <div className="apl-total-card">
                    <div className="apl-total-label">Coste mensual total estimado</div>
                    <div className="apl-total-value">{fmtMoney(totalEurCents, 'EUR')}</div>
                    <div className="apl-total-sub">
                        Suma de planes fijos + último consumo refrescado.
                        Otras monedas convertidas a EUR (tasa fija aproximada).
                    </div>
                </div>
                <div className="apl-actions-top">
                    <button
                        className="apl-btn-primary"
                        onClick={refreshAll}
                        disabled={refreshingAll}
                    >
                        {refreshingAll ? '⟳ Refrescando…' : '↻ Refrescar todas las APIs'}
                    </button>
                    <button
                        className="apl-btn-secondary"
                        onClick={() => setEditing({ __new: true })}
                    >
                        + Añadir plataforma
                    </button>
                </div>
            </div>

            <div className="apl-hint">
                💡 Los datos de consumo se quedan congelados desde el último refresh.
                Pulsa <strong>"Refrescar todas las APIs"</strong> antes de mirar el total para tener cifras actualizadas.
                {lastGlobalRefresh > 0 && (
                    <> Última actualización: <strong>{fmtRelativeTime(new Date(lastGlobalRefresh))}</strong>.</>
                )}
            </div>

            {error && <div className="apl-error">{error}</div>}
            {loading && <div className="apl-loading">Cargando plataformas…</div>}

            {!loading && (
                <div className="apl-list">
                    {platforms.map(p => (
                        <PlatformRow
                            key={p._id}
                            platform={p}
                            refreshing={refreshingId === p._id}
                            onRefresh={() => refreshOne(p._id)}
                            onEdit={() => setEditing(p)}
                            onDelete={() => removeOne(p)}
                        />
                    ))}
                    {platforms.length === 0 && (
                        <div className="apl-empty">
                            Sin plataformas configuradas. Pulsa "Añadir plataforma" para empezar.
                        </div>
                    )}
                </div>
            )}

            {editing && (
                <PlatformModal
                    platform={editing.__new ? null : editing}
                    availableAdapters={availableAdapters}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

// ─── Una fila de plataforma ────────────────────────────────────────
function PlatformRow({ platform, refreshing, onRefresh, onEdit, onDelete }) {
    const fixed = platform.fixedCostCents || 0;
    const usage = platform.lastUsageCents || 0;
    const total = (platform.pricingModel === 'fixed') ? fixed : (fixed + usage);

    // Render del logo con fallback a emoji si la URL falla o no existe.
    // Usamos onError para detectar 404/red caída y caer al emoji.
    const [logoFailed, setLogoFailed] = useState(false);
    const useLogo = platform.iconUrl && !logoFailed;

    return (
        <div className={`apl-row ${!platform.isActive ? 'apl-row-disabled' : ''}`}>
            <div className="apl-row-icon">
                {useLogo
                    ? <img
                        src={platform.iconUrl}
                        alt={platform.name}
                        className="apl-logo-img"
                        onError={() => setLogoFailed(true)}
                    />
                    : <span>{platform.icon}</span>}
            </div>
            <div className="apl-row-meta">
                <div className="apl-row-name">
                    {platform.name}
                    {platform.dashboardUrl && (
                        <a href={platform.dashboardUrl} target="_blank" rel="noreferrer" className="apl-row-link">↗</a>
                    )}
                </div>
                <div className="apl-row-tags">
                    <span className="apl-tag" style={{ color: PRICING_LABELS[platform.pricingModel]?.color }}>
                        {PRICING_LABELS[platform.pricingModel]?.label}
                    </span>
                    {platform.adapterKey && (
                        <span className="apl-tag apl-tag-api">API: {platform.adapterKey}</span>
                    )}
                </div>
                {platform.notes && (
                    <div className="apl-row-notes">{platform.notes}</div>
                )}
                {platform.lastRefreshError && (
                    platform.dashboardUrl ? (
                        <div className="apl-row-manual">
                            ℹ Coste no automatizable.{' '}
                            <a href={platform.dashboardUrl} target="_blank" rel="noreferrer">
                                Ver consumo ↗
                            </a>{' '}
                            y pulsa ✏️ para introducir el monto manualmente.
                        </div>
                    ) : (
                        <div className="apl-row-err">⚠ {platform.lastRefreshError}</div>
                    )
                )}
            </div>
            <div className="apl-row-cost">
                {platform.pricingModel !== 'usage' && (
                    <div className="apl-cost-line">
                        <span className="apl-cost-label">Plan:</span>
                        <strong>{fmtMoney(fixed, platform.currency)}/mes</strong>
                    </div>
                )}
                {(platform.pricingModel === 'usage' || platform.pricingModel === 'hybrid') && (
                    <div className="apl-cost-line">
                        <span className="apl-cost-label">Consumo:</span>
                        <strong>{fmtMoney(usage, platform.currency)}</strong>
                        <span className="apl-cost-when">
                            ({fmtRelativeTime(platform.lastRefreshedAt)})
                        </span>
                    </div>
                )}
                <div className="apl-cost-total">
                    Total mes: <strong>{fmtMoney(total, platform.currency)}</strong>
                </div>
            </div>
            <div className="apl-row-actions">
                {(platform.pricingModel === 'usage' || platform.pricingModel === 'hybrid') && platform.adapterKey && (
                    <button
                        className="apl-btn-mini"
                        onClick={onRefresh}
                        disabled={refreshing}
                        title="Refrescar consumo desde la API del proveedor"
                    >
                        {refreshing ? '⟳' : '↻'}
                    </button>
                )}
                <button className="apl-btn-mini" onClick={onEdit} title="Editar">✏️</button>
                <button className="apl-btn-mini apl-btn-mini-danger" onClick={onDelete} title="Eliminar">🗑️</button>
            </div>
        </div>
    );
}

// ─── Modal crear / editar ──────────────────────────────────────────
function PlatformModal({ platform, availableAdapters, onClose, onSaved }) {
    const isNew = !platform;
    const [form, setForm] = useState(() => ({
        name: platform?.name || '',
        slug: platform?.slug || '',
        icon: platform?.icon || '🔧',
        iconUrl: platform?.iconUrl || '',
        category: platform?.category || 'other',
        pricingModel: platform?.pricingModel || 'fixed',
        fixedCostCents: platform?.fixedCostCents || 0,
        currency: platform?.currency || 'EUR',
        adapterKey: platform?.adapterKey || '',
        dashboardUrl: platform?.dashboardUrl || '',
        notes: platform?.notes || '',
        order: platform?.order ?? 100,
        isActive: platform?.isActive !== false
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const submit = async () => {
        if (!form.name.trim()) { setError('Nombre requerido'); return; }
        if (isNew && !form.slug.trim()) { setError('Slug requerido (solo letras, números, _ y -)'); return; }
        setSaving(true);
        setError('');
        try {
            if (isNew) {
                await adminPlatformCreate(form);
            } else {
                await adminPlatformUpdate(platform._id, form);
            }
            onSaved();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="apl-modal-overlay" onClick={onClose}>
            <div className="apl-modal" onClick={e => e.stopPropagation()}>
                <div className="apl-modal-head">
                    <h3>{isNew ? 'Nueva plataforma' : 'Editar plataforma'}</h3>
                    <button className="apl-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="apl-modal-body">
                    <label className="apl-label">Nombre</label>
                    <input className="apl-input" value={form.name}
                           onChange={e => update('name', e.target.value)} />

                    <div className="apl-row-fields">
                        <div className="apl-field">
                            <label className="apl-label">Slug (único, sin espacios)</label>
                            <input
                                className="apl-input"
                                value={form.slug}
                                disabled={!isNew}
                                onChange={e => update('slug', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                placeholder="ej. anthropic"
                            />
                        </div>
                        <div className="apl-field apl-field-tiny">
                            <label className="apl-label">Emoji</label>
                            <input className="apl-input" value={form.icon} maxLength={4}
                                   onChange={e => update('icon', e.target.value)} />
                        </div>
                    </div>

                    <label className="apl-label">URL del logo (opcional, prevalece sobre emoji)</label>
                    <input
                        className="apl-input"
                        value={form.iconUrl}
                        onChange={e => update('iconUrl', e.target.value)}
                        placeholder="https://cdn.simpleicons.org/<slug>/<color> — ej. https://cdn.simpleicons.org/stripe/635BFF"
                    />
                    <div className="apl-hint-small">
                        Usa <a href="https://simpleicons.org" target="_blank" rel="noreferrer">simpleicons.org</a> para encontrar el slug. Formato: <code>https://cdn.simpleicons.org/&lt;slug&gt;/&lt;hexcolor&gt;</code>
                    </div>

                    <div className="apl-row-fields">
                        <div className="apl-field">
                            <label className="apl-label">Categoría</label>
                            <select className="apl-input" value={form.category}
                                    onChange={e => update('category', e.target.value)}>
                                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </div>
                        <div className="apl-field">
                            <label className="apl-label">Modelo de precio</label>
                            <select className="apl-input" value={form.pricingModel}
                                    onChange={e => update('pricingModel', e.target.value)}>
                                <option value="fixed">Plan fijo</option>
                                <option value="usage">Por consumo</option>
                                <option value="hybrid">Híbrido (fijo + extra)</option>
                            </select>
                        </div>
                    </div>

                    <div className="apl-row-fields">
                        <div className="apl-field">
                            <label className="apl-label">Coste fijo mensual (céntimos)</label>
                            <input className="apl-input" type="number" min="0" step="1"
                                   value={form.fixedCostCents}
                                   onChange={e => update('fixedCostCents', parseInt(e.target.value, 10) || 0)} />
                            <div className="apl-hint-small">
                                = {fmtMoney(form.fixedCostCents, form.currency)}/mes
                            </div>
                        </div>
                        <div className="apl-field apl-field-small">
                            <label className="apl-label">Moneda</label>
                            <select className="apl-input" value={form.currency}
                                    onChange={e => update('currency', e.target.value)}>
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                                <option value="GBP">GBP</option>
                            </select>
                        </div>
                    </div>

                    <label className="apl-label">
                        Adapter API (opcional, para consumo automático)
                    </label>
                    <select className="apl-input" value={form.adapterKey}
                            onChange={e => update('adapterKey', e.target.value)}>
                        <option value="">— sin adapter (manual / fijo) —</option>
                        {availableAdapters.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <div className="apl-hint-small">
                        Si seleccionas un adapter, el botón refresh llamará a la API real del proveedor.
                        Algunos requieren env vars adicionales en Render — ver detalles en cada adapter.
                    </div>

                    <label className="apl-label">URL del dashboard del proveedor</label>
                    <input className="apl-input" value={form.dashboardUrl}
                           placeholder="https://..."
                           onChange={e => update('dashboardUrl', e.target.value)} />

                    <label className="apl-label">Notas</label>
                    <textarea className="apl-input apl-textarea" value={form.notes} rows={3}
                              onChange={e => update('notes', e.target.value)} />

                    <div className="apl-row-fields">
                        <div className="apl-field">
                            <label className="apl-label">Orden visual</label>
                            <input className="apl-input" type="number" value={form.order}
                                   onChange={e => update('order', parseInt(e.target.value, 10) || 100)} />
                        </div>
                        <div className="apl-field apl-field-small apl-field-checkbox">
                            <label className="apl-label">
                                <input type="checkbox" checked={form.isActive}
                                       onChange={e => update('isActive', e.target.checked)} />
                                {' '}Activa
                            </label>
                        </div>
                    </div>

                    {error && <div className="apl-modal-err">{error}</div>}
                </div>
                <div className="apl-modal-actions">
                    <button className="apl-btn-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
                    <button className="apl-btn-primary" onClick={submit} disabled={saving}>
                        {saving ? 'Guardando…' : (isNew ? 'Crear' : 'Guardar')}
                    </button>
                </div>
            </div>
        </div>
    );
}
